import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import DurationDisplay from '../common/DurationDisplay';

const CATEGORY_FILTERS = [
  { value: 'all', label: 'Todas' },
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Programa' },
  { value: 'jingle', label: 'Jingle' },
  { value: 'spot', label: 'Spot' },
  { value: 'advertising', label: 'Publicidad' },
];

export default function MusicHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchHistory();
  }, [dateFrom, dateTo, page]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;

      const { data } = await api.get('/api/history/music', { params });
      setHistory(data.history || data);
    } catch (err) {
      console.error('[MusicHistory] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visibleHistory = history.filter((entry) => {
    if (categoryFilter !== 'all' && entry.category !== categoryFilter) return false;
    if (!q) return true;
    return (
      (entry.title || '').toLowerCase().includes(q) ||
      (entry.artist || '').toLowerCase().includes(q) ||
      (entry.program_name || '').toLowerCase().includes(q)
    );
  });

  const handleClearFilters = () => {
    setCategoryFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setPage(1);
  };

  const hasActiveFilters = categoryFilter !== 'all' || dateFrom || dateTo || search;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Historial de reproducción</h1>
        <button onClick={fetchHistory} className="btn btn-secondary btn-sm" title="Actualizar">
          ↻
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Buscar tema, artista o programa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-sm text-sm"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="input text-sm w-40"
        >
          {CATEGORY_FILTERS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="input text-sm w-40"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="input text-sm w-40"
        />
        {hasActiveFilters && (
          <button onClick={handleClearFilters} className="text-xs btn btn-outline btn-sm">
            Limpiar
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-dark-400">Cargando historial...</div>
      ) : history.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No hay registros de reproducción todavía.
          {hasActiveFilters && ' Probá limpiar los filtros.'}
        </div>
      ) : visibleHistory.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No se encontraron resultados para la búsqueda.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tema</th>
                <th>Artista</th>
                <th>Programa</th>
                <th>Categoría</th>
                <th>Duración</th>
              </tr>
            </thead>
            <tbody>
              {visibleHistory.map((entry) => (
                <tr key={entry.id}>
                  <td className="text-sm text-dark-400">
                    {entry.played_at ? format(parseISO(entry.played_at), 'PPp') : '—'}
                  </td>
                  <td className="font-medium text-dark-100">{entry.title || '—'}</td>
                  <td className="text-dark-300">{entry.artist || '—'}</td>
                  <td className="text-dark-300">{entry.program_name || '—'}</td>
                  <td>
                    {entry.category && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-dark-700 text-dark-300">
                        {entry.category}
                      </span>
                    )}
                  </td>
                  <td className="text-sm text-dark-400">
                    {entry.duration_seconds && <DurationDisplay seconds={entry.duration_seconds} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {history.length > 0 && (
        <div className="flex justify-between text-sm text-dark-400">
          <span>Page {page}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              className="btn btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={loading}
              className="btn btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
